import { type FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { BehaviorType } from '../../enum/BehaviorType';
import { ScoreBreakdownType, type TeamBehavior } from '../../../../lib/types/TeamBehavior';
import { Tooltip } from '../../../Tooltip';

type BehaviorBarProps = {
  teamBehaviorDetails: TeamBehavior;
  totalCount: number;
  onClick?: (behavior: ScoreBreakdownType, type: BehaviorType) => void;
};

export const BehaviorBar: FC<BehaviorBarProps> = ({ teamBehaviorDetails, totalCount, onClick }) => {
  const theme = useTheme();
  const { destructive: weakColor, neutralBar: neutralColor, success: strongColor } = theme.colors.prototype;
  const { weak, neutral, strong } = teamBehaviorDetails.learners;
  const total = totalCount || 0;
  const isZeroState = weak + neutral + strong === 0 || total === 0;

  // Each half of the track represents up to `total` learners, so a segment's
  // width as a percentage of its half is simply its share of the team.
  // Neutral straddles the centre, half on each side.
  const toHalfPct = (count: number) => (total > 0 ? (count / total) * 100 : 0);
  const weakWidth = toHalfPct(weak);
  const strongWidth = toHalfPct(strong);
  const neutralHalfWidth = toHalfPct(neutral / 2);

  const handleClick = (type: BehaviorType) => {
    if (onClick && !isZeroState) {
      onClick(teamBehaviorDetails.behaviour, type);
    }
  };

  return (
    <BehaviorBarContainer>
      <TeamMemberCount $align="right">{weak}</TeamMemberCount>
      <Track>
        <Half $align="flex-end">
          {weak > 0 && (
            <SegmentTooltip
              offset={10}
              placement="top"
              content={<TooltipContainer>{weak} weak</TooltipContainer>}
              $width={weakWidth}
            >
              <Segment
                $color={weakColor}
                $opacity={0.9}
                $radius="9999px 0 0 9999px"
                $clickable={!isZeroState}
                onClick={() => handleClick(BehaviorType.WEAK)}
              />
            </SegmentTooltip>
          )}
          {neutral > 0 && (
            <SegmentTooltip
              offset={10}
              placement="top"
              content={<TooltipContainer>{neutral} neutral</TooltipContainer>}
              $width={neutralHalfWidth}
            >
              <Segment
                $color={neutralColor}
                $opacity={0.7}
                $radius={weak > 0 ? '0' : '9999px 0 0 9999px'}
                $clickable={!isZeroState}
                onClick={() => handleClick(BehaviorType.NEUTRAL)}
              />
            </SegmentTooltip>
          )}
        </Half>

        <Divider />

        <Half $align="flex-start">
          {neutral > 0 && (
            <SegmentTooltip
              offset={10}
              placement="top"
              content={<TooltipContainer>{neutral} neutral</TooltipContainer>}
              $width={neutralHalfWidth}
            >
              <Segment
                $color={neutralColor}
                $opacity={0.7}
                $radius={strong > 0 ? '0' : '0 9999px 9999px 0'}
                $clickable={!isZeroState}
                onClick={() => handleClick(BehaviorType.NEUTRAL)}
              />
            </SegmentTooltip>
          )}
          {strong > 0 && (
            <SegmentTooltip
              offset={10}
              placement="top"
              content={<TooltipContainer>{strong} strong</TooltipContainer>}
              $width={strongWidth}
            >
              <Segment
                $color={strongColor}
                $opacity={0.9}
                $radius="0 9999px 9999px 0"
                $clickable={!isZeroState}
                onClick={() => handleClick(BehaviorType.STRONG)}
              />
            </SegmentTooltip>
          )}
        </Half>
      </Track>
      <TeamMemberCount $align="left">{strong}</TeamMemberCount>
    </BehaviorBarContainer>
  );
};

const BehaviorBarContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Track = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  height: 12px;
  border-radius: 9999px;
  background: color-mix(in srgb, ${({ theme }) => theme.colors.prototype.neutralBar} 20%, transparent);

  @media (max-width: 768px) {
    height: 16px;
  }
`;

const Half = styled.div<{ $align: 'flex-start' | 'flex-end' }>`
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => $align};
`;

const Divider = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  transform: translateX(-0.5px);
  background: color-mix(in srgb, ${({ theme }) => theme.colors.prototype.mutedForeground} 30%, transparent);
  z-index: 2;
`;

const SegmentTooltip = styled(Tooltip)<{ $width: number }>`
  width: ${({ $width }) => ($width ? `${$width}%` : '0')};
  height: 100%;
  display: flex;
  align-items: center;
  & > div {
    border-radius: 4px;
  }
`;

const Segment = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$color', '$opacity', '$radius', '$clickable'].includes(prop),
})<{ $color: string; $opacity: number; $radius: string; $clickable: boolean }>`
  width: 100%;
  height: 12px;
  background: ${({ $color }) => $color};
  opacity: ${({ $opacity }) => $opacity};
  border-radius: ${({ $radius }) => $radius};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: width 0.3s ease-in-out;

  @media (max-width: 768px) {
    height: 16px;
  }
`;

const TooltipContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  padding: 2px 4px;
  min-width: 60px;
  font-weight: 500;
`;

const TeamMemberCount = styled.span<{ $align: 'left' | 'right' }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  flex-shrink: 0;
  min-width: 20px;
  margin: 0 8px;
  text-align: ${({ $align }) => $align};
`;
